import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Minus, Plus, Check, Package, AlertTriangle } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useCurrency } from "@/context/CurrencyContext";
import { getAccessoryPhoto, getAccessoryVariantPhoto, getAccessoryGallery } from "@/lib/productPhotos";
import { getEffectiveStock, stockLabel } from "@/lib/stock";
import NotifyForm from "@/components/NotifyForm";
import ZoomOverlay from "./ZoomOverlay";

// ---------------------------------------------------------------------------
// Accessory (non-compound) product view. Simpler layout, pack-size selector,
// compatibility note, sharps disposal guidance. No COA / HPLC / lot tracing.
// Zoom-to-lightbox is enabled here because Accessories benefit from a
// closer look at hardware (needle gauge markings, blister packaging, etc.).
// ---------------------------------------------------------------------------
export default function AccessoryProductView({ product, qty, setQty }) {
  const navigate = useNavigate();
  const { addItem } = useCart();
  const { format: fmtPrice } = useCurrency();

  const variants = product.variants || [{ pack_size: 1, label: "Single", price: product.price }];
  const [variantIdx, setVariantIdx] = useState(0);
  const activeVariant = variants[variantIdx];
  const unitPrice = activeVariant.price / activeVariant.pack_size;
  const activeStock = getEffectiveStock(product, activeVariant.pack_size);
  const stockChip = stockLabel(activeStock);
  const isOOS = activeStock === 0;
  const canIncrease = activeStock === null || qty < activeStock;

  useEffect(() => {
    if (activeStock !== null && qty > activeStock) {
      setQty(Math.max(1, activeStock));
    }
  }, [variantIdx, activeStock, qty, setQty]);

  const addToCart = () => {
    if (isOOS) return;
    addItem(product, qty, activeVariant);
  };

  const buyNow = () => {
    if (isOOS) return;
    addToCart();
    navigate("/checkout");
  };

  const heroPhoto =
    getAccessoryVariantPhoto(product.slug, activeVariant.pack_size) ||
    getAccessoryPhoto(product.slug);

  // Optional multi-angle gallery for accessories that ship with several
  // marketing shots. Falls back to the single hero when null. Selected
  // thumb is tracked locally so pack-size changes don't reset the view.
  const gallery = getAccessoryGallery(product.slug);
  const galleryImages = gallery && gallery.length > 1 ? gallery : null;
  const [galleryIdx, setGalleryIdx] = useState(0);
  const activeImage = galleryImages ? galleryImages[galleryIdx] : heroPhoto;

  return (
    <div className="container-pd py-12 lg:py-16" data-testid="accessory-product-page">
      <div className="grid grid-cols-1 gap-12 lg:grid-cols-2">
        {/* Left, hero visual (no compound-photo tabs) */}
        <div>
          <div className="relative flex aspect-square items-center justify-center overflow-hidden rounded-2xl panel-warm bg-white p-8">
            {activeImage ? (
              <img
                src={activeImage}
                alt={product.name}
                className="h-full w-full object-contain object-center"
                draggable={false}
                data-testid="accessory-hero-photo"
              />
            ) : (
              <Package className="h-40 w-40 text-navy" strokeWidth={1.2} />
            )}
            <span className="absolute left-4 top-4 z-30 rounded-full bg-navy px-3 py-1 font-mono text-[10px] tracking-[0.14em] text-white">
              ACCESSORY
            </span>
            <ZoomOverlay
              enabled={!!activeImage}
              images={galleryImages || [activeImage].filter(Boolean)}
              index={galleryImages ? galleryIdx : 0}
              alt={product.name}
              hintTestId="accessory-zoom-hint"
            />
          </div>
          {galleryImages && (
            <div className="mt-3 flex gap-2" data-testid="accessory-thumb-row">
              {galleryImages.map((src, i) => (
                <button
                  key={src}
                  onClick={() => setGalleryIdx(i)}
                  onMouseEnter={() => setGalleryIdx(i)}
                  aria-pressed={galleryIdx === i}
                  className={`h-16 w-16 shrink-0 overflow-hidden rounded-lg border bg-white transition-colors ${
                    galleryIdx === i ? "border-brandblue ring-1 ring-brandblue/40" : "border-line hover:border-brandblue/60"
                  }`}
                  data-testid={`accessory-thumb-${i}`}
                >
                  <img src={src} alt="" className="h-full w-full object-contain p-1" loading="lazy" decoding="async" draggable={false} />
                </button>
              ))}
            </div>
          )}
          <p className="mt-4 text-xs text-subtle">
            Individually blister-sealed with a tamper strip. Boxed and shipped alongside compound orders at no extra cost.
          </p>
        </div>

        {/* Right, details */}
        <div>
          <p className="label-mono text-brandblue">↳ Accessories</p>
          <h1 className="mt-3 font-heading text-3xl font-black tracking-tight text-ink sm:text-4xl" data-testid="accessory-title">
            {product.name}
          </h1>
          <div className="mt-3 flex flex-wrap gap-2">
            {product.gauge && (
              <span className="inline-flex items-center rounded-full border border-line bg-white px-3 py-1 font-mono text-[11px] font-medium text-ink" data-testid="accessory-gauge">
                Gauge · {product.gauge}
              </span>
            )}
            {product.length && (
              <span className="inline-flex items-center rounded-full border border-line bg-white px-3 py-1 font-mono text-[11px] font-medium text-ink" data-testid="accessory-length">
                Length · {product.length}
              </span>
            )}
          </div>
          <p className="mt-4 text-base text-subtle">{product.short_description}</p>

          {product.compatibility_note && (
            <div
              className="mt-5 flex items-start gap-3 rounded-xl border border-brandblue/25 bg-brandblue/6 p-4"
              data-testid="accessory-compatibility"
            >
              <Check className="mt-0.5 h-4 w-4 shrink-0 text-brandblue" strokeWidth={2.5} />
              <p className="text-sm text-ink">
                <span className="font-semibold">Compatibility:</span> {product.compatibility_note}
              </p>
            </div>
          )}

          <div className="mt-6 rounded-xl panel-warm bg-white p-6">
            <p className="label-mono">§ Pack size</p>
            <div className="mt-3 grid grid-cols-3 gap-2" data-testid="accessory-pack-size-selector">
              {variants.map((v, i) => (
                <button
                  key={v.pack_size}
                  onClick={() => setVariantIdx(i)}
                  className={`rounded-lg border py-3 text-center transition-colors ${
                    variantIdx === i
                      ? "border-navy bg-navy text-white"
                      : "btn-outline"
                  }`}
                  data-testid={`pack-option-${v.pack_size}`}
                  aria-pressed={variantIdx === i}
                >
                  <span className="block font-heading text-lg font-black">{v.label}</span>
                  <span className={`mt-1 block font-mono text-[11px] ${variantIdx === i ? "text-white/80" : "text-subtle"}`}>
                    {fmtPrice(v.price)}
                  </span>
                </button>
              ))}
            </div>
            <div className="mt-5 flex items-baseline gap-3">
              <span className="font-heading text-4xl font-black text-ink" data-testid="accessory-active-price">
                {fmtPrice(activeVariant.price)}
              </span>
              <span className="text-xs text-subtle" data-testid="accessory-unit-price">
                ≈ {fmtPrice(unitPrice, { fractionDigits: 2 })} / {product.unit_label || "unit"}
              </span>
            </div>
            <p
              className={`mt-3 flex items-center gap-1.5 font-mono text-[11px] tracking-[0.14em] ${
                stockChip.tone === "oos"
                  ? "text-red-600"
                  : stockChip.tone === "low"
                  ? "text-amber-700"
                  : "text-emerald-700"
              }`}
              data-testid="accessory-stock-indicator"
            >
              <span
                className={`inline-block h-1.5 w-1.5 rounded-full ${
                  stockChip.tone === "oos"
                    ? "bg-red-500"
                    : stockChip.tone === "low"
                    ? "bg-amber-500"
                    : "bg-emerald-500"
                }`}
                aria-hidden="true"
              />
              {stockChip.text}
            </p>
            {isOOS ? (
              <div className="mt-5 rounded-xl border border-red-200 bg-red-50 p-5" data-testid="accessory-oos-panel">
                <p className="font-mono text-[11px] tracking-[0.18em] text-red-700">● OUT OF STOCK</p>
                <p className="mt-2 text-sm text-ink">
                  Leave your email and we&apos;ll notify you the moment this pack size is back.
                </p>
                <div className="mt-4">
                  <NotifyForm productId={product.id} slug={product.slug} context="restock" />
                </div>
              </div>
            ) : (
              <>
                <div className="mt-5 flex items-center gap-4">
                  <div className="flex items-center rounded-full border border-line">
                    <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="px-3 py-2 text-ink hover:text-brandblue" data-testid="qty-decrease">
                      <Minus className="h-4 w-4" />
                    </button>
                    <span className="w-8 text-center font-semibold" data-testid="qty-value">{qty}</span>
                    <button
                      onClick={() => setQty((q) => (activeStock === null ? q + 1 : Math.min(activeStock, q + 1)))}
                      disabled={!canIncrease}
                      className="px-3 py-2 text-ink hover:text-brandblue disabled:cursor-not-allowed disabled:text-line disabled:hover:text-line"
                      data-testid="qty-increase"
                      aria-label={canIncrease ? "Increase quantity" : "Reached available stock"}
                    >
                      <Plus className="h-4 w-4" />
                    </button>
                  </div>
                  <button onClick={addToCart} className="btn-pill flex-1 bg-navy py-3 text-sm font-semibold text-white hover:bg-navydark" data-testid="add-to-cart-button">
                    Add to cart →
                  </button>
                </div>
                {!canIncrease && (
                  <p className="mt-2 font-mono text-[10px] tracking-[0.14em] text-amber-700" data-testid="accessory-stock-cap-note">
                    MAX AVAILABLE, {activeStock} IN STOCK
                  </p>
                )}
                <button onClick={buyNow} className="btn-pill btn-outline mt-3 w-full py-3 text-sm font-semibold" data-testid="buy-now-button">
                  Buy now →
                </button>
              </>
            )}
          </div>

          {product.disposal_note && (
            <div
              className="mt-6 flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4"
              data-testid="accessory-disposal"
            >
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" strokeWidth={2} />
              <p className="text-sm text-ink">
                <span className="font-semibold text-amber-900">Sharps disposal:</span> {product.disposal_note}
              </p>
            </div>
          )}

          {product.long_description && (
            <p className="mt-6 text-sm leading-relaxed text-ink" data-testid="accessory-long-desc">
              {product.long_description}
            </p>
          )}

          <div className="mt-6 rounded-xl border border-line bg-[#FAFBFC] p-4">
            <p className="font-mono text-[10px] tracking-[0.18em] text-subtle">● NOT A RESEARCH COMPOUND</p>
            <p className="mt-2 text-xs text-subtle">
              Accessories carry no COA, no purity or HPLC data, those are specific to compounds.
              This item is hardware only, sold to support your pen-kit protocol.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
